import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

const USE_OF_FUNDS = [
  {
    pct: "38%",
    label: "Tannery Infrastructure",
    body: "Wet-blue and crust processing lines, water treatment, environmental controls and core utilities.",
  },
  {
    pct: "27%",
    label: "Manufacturing Facilities",
    body: "Bag, shoe and accessory production lines built to Italian luxury specification standards.",
  },
  {
    pct: "20%",
    label: "Machinery & Equipment",
    body: "Tannery drums, finishing presses, cutting and stitching technology imported from Italian suppliers.",
  },
  {
    pct: "10%",
    label: "Training & Skills Transfer",
    body: "On-site Italian master tanners and craftspeople training a Botswana-based workforce.",
  },
  {
    pct: "5%",
    label: "Working Capital",
    body: "Reserve for hide procurement, logistics, and ramp to first export shipments.",
  },
];

const PARTICIPATION = [
  {
    code: "EQ",
    title: "Equity Participation",
    body: "Direct equity into LRG Leather Botswana SPV. Tiered for institutional, family office and strategic investors with structured exit pathways.",
    bullets: [
      "Senior equity tranche",
      "Co-investment rights",
      "Board observation seats (institutional)",
    ],
  },
  {
    code: "ST",
    title: "Strategic Manufacturing Partners",
    body: "For Italian and European luxury houses seeking secured production capacity in Africa with audited quality standards.",
    bullets: [
      "Capacity reservation agreements",
      "Joint quality protocols",
      "Co-branded production runs",
    ],
  },
  {
    code: "OF",
    title: "Offtake & Distribution",
    body: "Long-term purchase agreements for finished leather goods and finished hides into EU, US and global luxury distribution.",
    bullets: [
      "Multi-year offtake contracts",
      "Exclusive territory options",
      "Volume-based pricing tiers",
    ],
  },
];

export default function Investment() {
  return (
    <div data-testid="page-investment" className="bg-[#F9F9F6]">
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow">02 / Investment Structure</p>
            <h1
              data-testid="investment-headline"
              className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight mt-6 rise"
            >
              The capital
              <br />
              structure.
              <span className="italic text-[#5C2018]"> Bankable.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-[#1A1A1A] max-w-2xl leading-relaxed">
              A clearly scoped Special Purpose Vehicle, a defined capital
              requirement, and three structured pathways for institutional
              and strategic participation.
            </p>
          </div>
        </div>
      </section>

      {/* SPV Headline strip */}
      <section
        data-testid="spv-strip"
        className="border-y border-[#1A1A1A] bg-[#F9F9F6]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">SPV Name</p>
            <p className="font-serif text-3xl md:text-4xl tracking-tight mt-3">
              LRG Leather Botswana
            </p>
          </div>
          <div className="col-span-12 md:col-span-3 md:border-l md:border-[#E5E5E0] md:pl-8">
            <p className="eyebrow">Capital Requirement</p>
            <p className="font-serif text-3xl md:text-4xl tracking-tight mt-3">
              $45M<span className="text-[#666666] text-base ml-2">USD</span>
            </p>
          </div>
          <div className="col-span-12 md:col-span-3 md:border-l md:border-[#E5E5E0] md:pl-8">
            <p className="eyebrow">Jurisdiction</p>
            <p className="font-serif text-3xl md:text-4xl tracking-tight mt-3">
              Botswana SEZA
            </p>
          </div>
        </div>
      </section>

      {/* Use of Funds */}
      <section
        data-testid="use-of-funds-section"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32"
      >
        <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-5">
            <p className="eyebrow">Allocation</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
              Use of funds.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-base text-[#1A1A1A] leading-relaxed max-w-xl">
              Capital flows directly into productive infrastructure,
              equipment and human capital. Minimal overhead by design.
              Indicative breakdown subject to final SPV documentation.
            </p>
          </div>
        </div>

        <div className="border-t border-[#1A1A1A]">
          {USE_OF_FUNDS.map((row, idx) => (
            <div
              key={row.label}
              data-testid={`fund-row-${idx}`}
              className="border-b border-[#E5E5E0] grid grid-cols-12 gap-6 py-8 md:py-10 group hover:bg-white transition-colors duration-300"
            >
              <div className="col-span-3 md:col-span-2 font-serif text-3xl md:text-5xl text-[#5C2018] tracking-tight">
                {row.pct}
              </div>
              <div className="col-span-9 md:col-span-4">
                <h3 className="font-serif text-2xl md:text-[28px] tracking-tight">
                  {row.label}
                </h3>
              </div>
              <div className="col-span-12 md:col-span-5 md:col-start-8">
                <p className="text-base text-[#1A1A1A] leading-relaxed max-w-md">
                  {row.body}
                </p>
              </div>
              {/* Visual bar */}
              <div className="col-span-12">
                <div className="h-px w-full bg-[#E5E5E0] relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-[#5C2018] line-grow"
                    style={{
                      width: row.pct,
                      animationDelay: `${0.1 * idx}s`,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Participation Options */}
      <section
        data-testid="participation-section"
        className="bg-[#1A1A1A] text-[#F9F9F6]"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow text-[#A8A8A0]">Pathways</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
                Investor
                <br />
                participation
                <br />
                options.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
              <p className="text-base text-[#C7C7BF] leading-relaxed">
                Three engagement structures. Designed for the way capital,
                manufacturing partners and offtake buyers actually approach
                an integrated project of this kind.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-[#333330]">
            {PARTICIPATION.map((opt, idx) => (
              <div
                key={opt.code}
                data-testid={`participation-${opt.code.toLowerCase()}`}
                className="border-r border-b border-[#333330] p-8 md:p-10 hover:bg-[#0E0E0E] transition-colors duration-300 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-[#E5C4A1] text-2xl tracking-tight">
                    0{idx + 1}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#A8A8A0] border border-[#333330] px-3 py-1">
                    {opt.code}
                  </span>
                </div>
                <h3 className="font-serif text-3xl tracking-tight leading-tight mt-10">
                  {opt.title}
                </h3>
                <p className="mt-5 text-sm text-[#C7C7BF] leading-relaxed">
                  {opt.body}
                </p>
                <ul className="mt-8 space-y-3">
                  {opt.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-sm text-[#F9F9F6]"
                    >
                      <Check
                        size={14}
                        className="mt-1 flex-shrink-0 text-[#E5C4A1]"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-10">
                  <Link
                    to="/contact"
                    data-testid={`participation-cta-${opt.code.toLowerCase()}`}
                    className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] font-semibold text-[#F9F9F6] border-b border-[#F9F9F6] pb-1 hover:text-[#E5C4A1] hover:border-[#E5C4A1] transition-colors"
                  >
                    Engage on this track
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capital Structure Options A/B/C — phased entry */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-[#E5E5E0]" data-testid="capital-options-section">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow">Capital Structure</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-5">
              Three entry points.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-base text-[#1A1A1A] leading-relaxed">
              Investors can enter the platform at three different scopes — from a Phase 1 foundation position to full vertical integration of the leather park.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-[#1A1A1A]">
          {[
            {
              code: "A",
              title: "Phase 1 Entry",
              subtitle: "Foundation investment",
              body: "Fastest path to revenue. Wet blue sourcing and initial processing, anchored against BMC supply.",
              bullets: [
                "Wet blue sourcing & processing",
                "Small manufacturing unit",
                "Training centre",
                "Fastest path to revenue",
              ],
            },
            {
              code: "B",
              title: "Phase 1 + 2",
              subtitle: "Manufacturing scale",
              body: "All Phase 1 operations plus finished leather production with Italian-grade equipment and export channel development.",
              bullets: [
                "All Phase 1 operations",
                "Finished leather production",
                "Export channel development",
                "Quality certification",
              ],
              primary: true,
            },
            {
              code: "C",
              title: "Full Leather Park",
              subtitle: "Long-term industrial development",
              body: "Complete vertical integration of tannery, finishing, luxury manufacturing, training and export logistics. Maximum value capture.",
              bullets: [
                "Complete vertical integration",
                "Full industrial ecosystem",
                "Regional hub positioning",
                "Maximum value capture",
              ],
            },
          ].map((o) => (
            <div
              key={o.code}
              data-testid={`capital-option-${o.code.toLowerCase()}`}
              className={`border-r border-b border-[#1A1A1A] p-8 md:p-10 flex flex-col transition-colors ${
                o.primary ? "bg-[#5C2018] text-[#F9F9F6]" : "bg-[#F9F9F6] hover:bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-serif text-2xl ${o.primary ? "text-[#E5C4A1]" : "text-[#5C2018]"}`}>
                  Option {o.code}
                </span>
                <span className={`text-[10px] uppercase tracking-[0.22em] px-3 py-1 border ${
                  o.primary ? "border-[#E5C4A1] text-[#E5C4A1]" : "border-[#1A1A1A] text-[#1A1A1A]"
                }`}>
                  {o.code}
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight mt-10">
                {o.title}
              </h3>
              <p className={`eyebrow mt-2 ${o.primary ? "text-[#E5C4A1]" : ""}`}>{o.subtitle}</p>
              <p className={`text-sm leading-relaxed mt-5 ${o.primary ? "text-[#E0D8CD]" : "text-[#1A1A1A]"}`}>
                {o.body}
              </p>
              <ul className="mt-6 space-y-3">
                {o.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm">
                    <Check size={14} className={`mt-1 flex-shrink-0 ${o.primary ? "text-[#E5C4A1]" : "text-[#5C2018]"}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Investment Thesis + Economic Impact */}
      <section className="bg-[#1A1A1A] text-[#F9F9F6]" data-testid="investment-thesis-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-6">
              <p className="eyebrow text-[#A8A8A0]">Investment Thesis</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.02] mt-5">
                Structured for institutional capital.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#333330] border border-[#333330] mb-16">
            {[
              "SEZA Investor Licence granted (No. SEZBW00029, 16 Mar 2026, 50-year validity)",
              "Phased capital deployment reduces risk at every stage",
              "Existing raw material base — no supply chain invention required",
              "SEZA alignment provides regulatory framework and incentives",
              "Clear export pathway to established global markets",
              "Named Italian technical partners de-risk operational execution",
            ].map((t, i) => (
              <div key={t} className="bg-[#1A1A1A] p-6 md:p-8 flex gap-5">
                <span className="font-serif text-[#E5C4A1] text-xl flex-shrink-0">0{i + 1}</span>
                <p className="font-serif text-lg md:text-xl tracking-tight leading-snug">{t}</p>
              </div>
            ))}
          </div>

          {/* Economic impact */}
          <p className="eyebrow text-[#A8A8A0] mb-6">Projected Economic Impact</p>
          <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#333330]">
            {[
              ["2,000-5,000", "Direct Jobs Created"],
              ["500+", "Annual Training Places"],
              ["$100M+", "Export Revenue Potential"],
              ["200-300K", "Hides Processed Annually"],
              ["$50M+", "Manufacturing Output"],
              ["100+", "SME Suppliers"],
              ["40%+", "Women & Youth Workforce"],
              ["6", "Export Markets Served"],
            ].map(([v, k]) => (
              <div key={k} className="border-r border-b border-[#333330] p-5 md:p-7">
                <p className="font-serif text-2xl md:text-3xl text-[#E5C4A1] tracking-tight">{v}</p>
                <p className="eyebrow text-[#A8A8A0] mt-3">{k}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#A8A8A0] mt-6 max-w-2xl">
            Projected ranges based on phased development planning. Final figures subject to feasibility study and market conditions.
          </p>
        </div>
      </section>

      {/* Closing strip */}
      <section
        data-testid="investment-cta"
        className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32"
      >
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow">Continue</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-5">
              See the execution
              <br />
              & partner architecture.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end gap-4">
            <Link
              to="/execution"
              data-testid="investment-to-execution-cta"
              className="inline-flex items-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] transition-colors duration-300"
            >
              Execution Structure
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
